import { ChangeEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { formatNumber } from "../../utils";
import ProductPicker, { ProductOption } from "../Bdc/Settings/ProductPicker";
import {
  NoteLineDraft,
  newCatalogueLine,
  sanitizeInteger,
  sanitizeMoney,
  toLineInput,
} from "./noteDraft";
import { noteLineTotal } from "./totals";

interface NoteLinesEditorProps {
  lines: NoteLineDraft[];
  error?: string;
  onChange: (lines: NoteLineDraft[]) => void;
}

type FigureField = "qty" | "unitPrice" | "discountAmount";

const FIGURE_FIELDS: FigureField[] = ["qty", "unitPrice", "discountAmount"];

const FIGURE_SANITIZERS: Record<FigureField, (text: string) => string> = {
  qty: sanitizeInteger,
  unitPrice: sanitizeMoney,
  discountAmount: sanitizeMoney,
};

const figureInputSx = { width: 110 };
const figureInputProps = {
  htmlInput: { inputMode: "decimal", style: { textAlign: "right" } },
} as const;

type FigureChangeHandler = (event: ChangeEvent<HTMLInputElement>) => void;

const pickedProduct = (line: NoteLineDraft): ProductOption | null =>
  line.productUuid
    ? { uuid: line.productUuid, code: line.productCode, name: line.productName }
    : null;

function NoteLineRow({
  line,
  lineNo,
  onUpdate,
  onRemove,
}: Readonly<{
  line: NoteLineDraft;
  lineNo: number;
  onUpdate: (line: NoteLineDraft) => void;
  onRemove: (key: string) => void;
}>) {
  const handleProductChange = (option: ProductOption | null) =>
    onUpdate({
      ...line,
      productUuid: option?.uuid ?? null,
      productCode: option?.code ?? "",
      productName: option?.name ?? "",
    });
  const figureChangeHandler =
    (field: FigureField): FigureChangeHandler =>
    (event) =>
      onUpdate({ ...line, [field]: FIGURE_SANITIZERS[field](event.target.value) });
  const figureChangeHandlers: Record<FigureField, FigureChangeHandler> = {
    qty: figureChangeHandler("qty"),
    unitPrice: figureChangeHandler("unitPrice"),
    discountAmount: figureChangeHandler("discountAmount"),
  };
  const handleRemove = () => onRemove(line.key);

  return (
    <TableRow>
      <TableCell>{lineNo}</TableCell>
      <TableCell sx={{ minWidth: 280 }}>
        <ProductPicker
          value={pickedProduct(line)}
          initialQuery=""
          onChange={handleProductChange}
        />
      </TableCell>
      {FIGURE_FIELDS.map((field) => (
        <TableCell key={field} align="right">
          <TextField
            size="small"
            value={line[field]}
            placeholder="0"
            onChange={figureChangeHandlers[field]}
            sx={figureInputSx}
            slotProps={figureInputProps}
          />
        </TableCell>
      ))}
      <TableCell align="right">{formatNumber(noteLineTotal(toLineInput(line)))}</TableCell>
      <TableCell align="center">
        <Tooltip title="ลบบรรทัด">
          <IconButton size="small" onClick={handleRemove} aria-label={`ลบบรรทัด ${lineNo}`}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/**
 * The lines of a note keyed without a bill: each picks a product from the catalogue. Line
 * totals here are a preview — the server prices every line again when the note is saved.
 */
export default function NoteLinesEditor({
  lines,
  error,
  onChange,
}: Readonly<NoteLinesEditorProps>) {
  const handleUpdate = (updated: NoteLineDraft) =>
    onChange(lines.map((line) => (line.key === updated.key ? updated : line)));
  const handleRemove = (key: string) => onChange(lines.filter((line) => line.key !== key));
  const handleAdd = () => onChange([...lines, newCatalogueLine()]);

  return (
    <Stack gap={1}>
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              <TableCell width={48}>#</TableCell>
              <TableCell>สินค้า</TableCell>
              <TableCell align="right">จำนวน</TableCell>
              <TableCell align="right">ราคา/หน่วย</TableCell>
              <TableCell align="right">ส่วนลด</TableCell>
              <TableCell align="right">จำนวนเงิน</TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <NoteLineRow
                key={line.key}
                line={line}
                lineNo={index + 1}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row">
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
          เพิ่มสินค้า
        </Button>
      </Stack>
    </Stack>
  );
}
